import React, { useEffect, useState, useCallback } from "react";
import { api, todayISO, formatDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import DatePicker from "@/components/DatePicker";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  ChefHat, Sparkles, Plus, CheckCircle2, Loader2, Circle, ListChecks, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const ESTADO_STYLE = {
  pendiente: "bg-[#F0F0F0] text-[#595959] border-[#E0E0E0]",
  en_proceso: "bg-[#FDF3E1] text-[#A6751F] border-[#F0DEB2]",
  terminado: "bg-[#DAE6D7] text-[#3F6B3C] border-[#BCD3B7]",
};
const ESTADO_LABEL = {
  pendiente: "Pendiente",
  en_proceso: "En proceso",
  terminado: "Terminado",
};

export default function Produccion() {
  const { user } = useAuth();
  const isElaborador = user?.role === "elaborador";
  const canCreate = user?.role === "admin" || user?.role === "encargado";

  const [fecha, setFecha] = useState(todayISO());
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  // add item modal
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ producto_id: "", producto_nombre: "", cantidad_planeada: 1, unidad: "u" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/cocina/produccion-hoy", { params: { fecha } });
      setPlan(r.data.plan);
    } finally { setLoading(false); }
  }, [fecha]);

  useEffect(() => { load(); }, [load]);

  const generar = async () => {
    if (!canCreate) return;
    setGenerating(true);
    try {
      const r = await api.post(`/produccion/generar?fecha=${fecha}`);
      setPlan(r.data);
      const n = r.data.items.length;
      if (n === 0) toast.info("No hay pedidos confirmados para esa fecha");
      else toast.success(`Plan generado con ${n} ítem${n === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al generar");
    } finally { setGenerating(false); }
  };

  const setItemState = async (item, estado) => {
    try {
      const body = { estado };
      // if marking terminado, default cantidad_real to planeada if 0
      if (estado === "terminado" && (!item.cantidad_real || item.cantidad_real === 0)) {
        body.cantidad_real = item.cantidad_planeada;
      }
      const r = await api.put(`/produccion/${plan.id}/items/${item.id}`, body);
      setPlan(r.data);
      if (estado === "terminado") toast.success(`Terminado: ${item.producto_nombre}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const setCantidadReal = async (item, value) => {
    try {
      const r = await api.put(`/produccion/${plan.id}/items/${item.id}`, {
        cantidad_real: Number(value),
      });
      setPlan(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const removeItem = async (item) => {
    try {
      const r = await api.delete(`/produccion/${plan.id}/items/${item.id}`);
      setPlan(r.data);
      toast.success("Ítem removido");
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const addItem = async () => {
    if (!addForm.producto_nombre.trim()) { toast.error("Nombre obligatorio"); return; }
    try {
      const r = await api.post(`/produccion/${plan.id}/items`, {
        ...addForm,
        cantidad_planeada: Number(addForm.cantidad_planeada),
      });
      setPlan(r.data);
      setAddOpen(false);
      setAddForm({ producto_id: "", producto_nombre: "", cantidad_planeada: 1, unidad: "u" });
      toast.success("Ítem agregado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al agregar");
    }
  };

  const removePlan = async () => {
    try {
      await api.delete(`/produccion/${plan.id}`);
      setPlan(null);
      toast.success("Plan eliminado");
    } catch {
      toast.error("Error al eliminar plan");
    }
  };

  const items = plan?.items || [];
  const stats = {
    total: items.length,
    pendiente: items.filter((i) => i.estado === "pendiente").length,
    en_proceso: items.filter((i) => i.estado === "en_proceso").length,
    terminado: items.filter((i) => i.estado === "terminado").length,
    cantidad_total: items.reduce((s, i) => s + Number(i.cantidad_planeada || 0), 0),
  };

  return (
    <div>
      <PageHeader
        eyebrow="Producción"
        title={isElaborador ? "Producción del día" : "Producción"}
        subtitle="Plan diario armado desde los pedidos confirmados. Marcá los ítems que vas terminando."
      >
        <div className="flex items-center gap-2">
          <div className="w-36 shrink-0">
            <DatePicker value={fecha} onChange={(v) => setFecha(v || todayISO())} testid="prod-fecha" allowClear={false} />
          </div>
          {canCreate && (
            <Button
              className="bg-[#C85A32] hover:bg-[#A64A29] text-white gap-2 h-10"
              onClick={generar}
              disabled={generating}
              data-testid="prod-generar"
            >
              <Sparkles className="h-4 w-4" />
              <span className="hidden sm:inline">{generating ? "Generando..." : "Generar"}</span>
            </Button>
          )}
        </div>
      </PageHeader>

      {loading ? (
        <Card className="p-10 text-center text-[#8C8C8C] border-[#E5E0D8] rounded-2xl">Cargando...</Card>
      ) : !plan ? (
        <Card className="p-10 text-center border-[#E5E0D8] rounded-2xl bg-white">
          <ChefHat className="h-12 w-12 mx-auto text-[#C85A32] opacity-50 mb-3" />
          <div className="font-display font-semibold text-lg">Sin plan para {formatDate(fecha)}</div>
          <p className="text-sm text-[#595959] mt-1">
            {canCreate ? 'Tocá "Generar" para crear el plan desde los pedidos confirmados de esa fecha.' : "El encargado todavía no generó el plan."}
          </p>
        </Card>
      ) : (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <Stat label="Ítems" value={stats.total} icon={ListChecks} />
            <Stat label="Pendientes" value={stats.pendiente} color="text-[#595959]" />
            <Stat label="En proceso" value={stats.en_proceso} color="text-[#A6751F]" />
            <Stat label="Terminados" value={stats.terminado} color="text-[#3F6B3C]" />
          </div>

          {/* Header row with actions */}
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="text-sm text-[#595959]">
              Total unidades planeadas: <span className="font-mono font-bold text-[#1A1A1A]">{stats.cantidad_total}</span>
            </div>
            {canCreate && (
              <div className="flex gap-2">
                <Button variant="outline" className="border-[#E5E0D8] gap-2 h-9" onClick={() => setAddOpen(true)} data-testid="prod-add-item">
                  <Plus className="h-4 w-4" /> Agregar ítem
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" className="text-[#8E2E2E] hover:bg-[#FBEBEB] h-9 gap-2" data-testid="prod-delete-plan">
                      <Trash2 className="h-4 w-4" /> Borrar plan
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Borrar plan</AlertDialogTitle>
                      <AlertDialogDescription>¿Eliminar el plan de {formatDate(fecha)}? Esto no afecta el stock ya generado.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Volver</AlertDialogCancel>
                      <AlertDialogAction onClick={removePlan} className="bg-[#B23A3A] hover:bg-[#8E2E2E]">Eliminar</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}
          </div>

          {/* Items list */}
          <Card className="border-[#E5E0D8] rounded-2xl bg-white overflow-hidden">
            {items.length === 0 ? (
              <div className="p-10 text-center">
                <div className="font-display font-semibold text-base">Sin ítems</div>
                <p className="text-xs text-[#595959] mt-1">El plan está vacío. Agregá ítems o regenerá desde pedidos.</p>
              </div>
            ) : (
              <div className="divide-y divide-[#F0EBE0]">
                {items.map((it) => (
                  <div key={it.id} className={`p-3 sm:p-4 ${it.estado === "terminado" ? "bg-[#FBFCF9]" : ""}`} data-testid={`prod-item-${it.id}`}>
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className={`font-display font-semibold ${it.estado === "terminado" ? "line-through text-[#8C8C8C]" : "text-[#1A1A1A]"}`}>
                            {it.producto_nombre}
                          </div>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-[0.1em] border ${ESTADO_STYLE[it.estado]}`}>
                            {ESTADO_LABEL[it.estado]}
                          </span>
                        </div>
                        <div className="text-[12px] text-[#595959] mt-0.5 font-mono">
                          Planeado: <span className="font-bold text-[#1A1A1A]">{it.cantidad_planeada}</span> {it.unidad}
                          {it.cantidad_real > 0 && (
                            <> · Real: <span className="font-bold text-[#3F6B3C]">{it.cantidad_real}</span> {it.unidad}</>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {it.estado !== "terminado" && (
                          <>
                            {it.estado === "pendiente" && (
                              <Button
                                size="sm" variant="outline"
                                className="border-[#F0DEB2] text-[#A6751F] hover:bg-[#FDF3E1] h-8"
                                onClick={() => setItemState(it, "en_proceso")}
                                data-testid={`prod-start-${it.id}`}
                              >
                                <Loader2 className="h-3.5 w-3.5 mr-1" /> Empezar
                              </Button>
                            )}
                            <Button
                              size="sm"
                              className="bg-[#3F6B3C] hover:bg-[#2F5A2C] text-white h-8"
                              onClick={() => setItemState(it, "terminado")}
                              data-testid={`prod-finish-${it.id}`}
                            >
                              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Terminar
                            </Button>
                          </>
                        )}
                        {it.estado === "terminado" && (
                          <Button
                            size="sm" variant="ghost"
                            className="text-[#8C8C8C] hover:text-[#A6751F] h-8"
                            onClick={() => setItemState(it, "en_proceso")}
                            data-testid={`prod-reopen-${it.id}`}
                          >
                            <Circle className="h-3.5 w-3.5 mr-1" /> Reabrir
                          </Button>
                        )}
                        {canCreate && (
                          <button type="button" onClick={() => removeItem(it)} className="h-8 w-8 rounded-md hover:bg-[#FBEBEB] flex items-center justify-center" data-testid={`prod-remove-${it.id}`}>
                            <Trash2 className="h-3.5 w-3.5 text-[#8E2E2E]" />
                          </button>
                        )}
                      </div>
                    </div>
                    {/* Cantidad real input */}
                    {(it.estado !== "pendiente" || isElaborador) && (
                      <div className="mt-2 flex items-center gap-2">
                        <Label className="text-[11px] text-[#8C8C8C] m-0">Producido:</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={it.cantidad_real || ""}
                          onChange={(e) => {
                            // optimistic local update
                            const updated = items.map((x) => x.id === it.id ? { ...x, cantidad_real: e.target.value } : x);
                            setPlan({ ...plan, items: updated });
                          }}
                          onBlur={(e) => setCantidadReal(it, e.target.value)}
                          className="h-8 w-24 font-mono"
                          data-testid={`prod-real-${it.id}`}
                        />
                        <span className="text-[11px] text-[#8C8C8C]">{it.unidad}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}

      {/* Add item dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Agregar ítem al plan</DialogTitle>
            <DialogDescription>Útil para producción extra que no salió de un pedido.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Producto</Label>
              <Input value={addForm.producto_nombre} onChange={(e) => setAddForm({ ...addForm, producto_nombre: e.target.value })} data-testid="prod-add-nombre" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Cantidad</Label>
                <Input type="number" value={addForm.cantidad_planeada} onChange={(e) => setAddForm({ ...addForm, cantidad_planeada: e.target.value })} data-testid="prod-add-cantidad" />
              </div>
              <div>
                <Label>Unidad</Label>
                <Input value={addForm.unidad} onChange={(e) => setAddForm({ ...addForm, unidad: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancelar</Button>
            <Button onClick={addItem} className="bg-[#C85A32] hover:bg-[#A64A29] text-white" data-testid="prod-add-save">Agregar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, color = "text-[#1A1A1A]", icon: Icon }) {
  return (
    <Card className="p-3 sm:p-4 border-[#E5E0D8] rounded-2xl bg-white">
      <div className="text-[10px] uppercase tracking-wider text-[#8C8C8C] flex items-center gap-1.5">
        {Icon && <Icon className="h-3.5 w-3.5" />}
        {label}
      </div>
      <div className={`font-display font-bold text-2xl sm:text-3xl ${color} mt-1`}>{value}</div>
    </Card>
  );
}
